import { createSelector } from "reselect";

import { ApplicationScope } from "@superblocksteam/shared";
import type {
  ApplicationConfiguration,
  ApplicationSettings,
  RouteDef,
} from "@superblocksteam/shared";

import { getRoutes } from "legacy/selectors/routeSelectors";
import type { AppState } from "store/types";
import { toUnscopedEntity } from "store/utils/scope";

import { getScopedEvents } from "./events/selectors";
import { getScopedStateVars } from "./stateVars/selectors";
import type { AppStateVar } from "./stateVars/StateConstants";
import { getScopedTimers } from "./timers/selectors";
import type { AppTimer } from "./timers/TimerConstants";

export const getCurrentApplication = (state: AppState) => {
  return state.application.wrapper.currentApplication;
};

export const getEnvironment = (state: AppState) =>
  state.application.wrapper.environment;

export const getApplicationSettings = (
  state: AppState,
): ApplicationSettings | undefined =>
  state.application.wrapper?.currentApplication?.settings;

export const selectCurrentApplicationSignatureTree = (state: AppState) =>
  state.application.wrapper?.currentApplicationSignatureTree;

export const getAppRepoConnection = (state: AppState) =>
  state.application.wrapper.currentApplication?.repoConnection;

export const getMergedAppConfiguration = createSelector(
  [
    (state: AppState) =>
      state.application.wrapper.currentApplication?.configuration,
    (state: AppState) => getRoutes(state),
    (state: AppState) => getScopedStateVars(state, ApplicationScope.APP),
    (state: AppState) => getScopedTimers(state, ApplicationScope.APP),
    (state: AppState) => getScopedEvents(state, ApplicationScope.APP),
  ],
  (
    configuration,
    routes,
    stateVars,
    timers,
    events,
  ): ApplicationConfiguration & { routes: Record<string, RouteDef> } => {
    return {
      ...(configuration ?? {}),
      routes,
      dsl: {
        ...(configuration?.dsl ?? { version: 8 }),
        stateVars: {
          stateVarMap: toUnscopedEntity<AppStateVar>(stateVars),
        },
        timers: {
          timerMap: toUnscopedEntity<AppTimer>(timers),
        },
        events: {
          eventMap: toUnscopedEntity(events),
        },
      },
    };
  },
);
